import { AxiosResponse } from "axios";
import { IPaginatedResponse } from "../../../types";
import { filterNonNull } from "../../../utils";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  ICheckout,
  ICheckoutResult,
  ICheckoutV2,
  IPayment,
  IPaymentsQuery,
  IPlatform,
} from "./payments.types";
import qs from "qs";

class PaymentService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Lists payments.
   *
   * @param query The query to filter the results by.
   * @returns A promise that resolves to the server's response containing the list of payments.
   */
  async list<T = IPayment>(
    query?: IPaymentsQuery
  ): Promise<AxiosResponse<IPaginatedResponse<T>>> {
    return this.request({
      method: "GET",
      url: `/api/v1/payments?${qs.stringify(filterNonNull(query || {}))}`,
    });
  }

  /**
   * Initializes a payment
   *
   * @param payload the checkout payload
   * @returns response from the server
   */
  async checkout(payload: ICheckout): Promise<AxiosResponse> {
    return this.request({
      method: "POST",
      url: "/api/v1/payments/checkout",
      data: payload,
    });
  }

  /**
   * Fetches the list of payment gateways that are ready to be used for checkout.
   * @param payload the platform to check
   * @returns response from the server
   */
  async ready(payload: IPlatform): Promise<AxiosResponse> {
    return this.request({
      method: "GET",
      url: "/api/v1/payments/ready",
      headers: {
        "X-Platform": payload.platform,
      },
    });
  }

  /**
   * Initializes a new payment checkout using the enhanced V2 API.
   * This method supports both subscription purchases and TVOD (Transactional Video on Demand) payments.
   *
   * @param payload - The checkout configuration object
   * @param payload.type - The type of payment: "subscription" for recurring subscriptions or "tvod" for one-time content purchases
   * @param payload.productId - Required for TVOD payments. The ID of the content/product being purchased
   * @param payload.subscriptionId - Required for subscription payments. The ID of the subscription plan
   * @param payload.currency - The currency for the payment (currently only "INR" is supported)
   * @param payload.provider - The payment gateway provider (e.g., "razorpay", "stripe", "phonepe", "ccavenue")
   * @param payload.coupon_code - Optional coupon code for discounts
   * @param payload.metadata - Optional metadata object for provider-specific configurations
   * @param payload.metadata.isPaymentInitiatedEnabled - Optional flag for PhonePe payment initiation
   *
   * @returns Promise that resolves to an AxiosResponse containing the checkout result
   * @returns Returns an object with:
   *   - status: boolean indicating if the checkout was initialized successfully
   *   - price: The original price before any discounts
   *   - real_price: The final price after applying discounts/coupons
   *   - currency: The currency code (e.g., "INR")
   *   - provider: The payment gateway provider name
   *   - orderId: Unique order identifier for tracking the payment
   *   - publicKey: Public key for client-side payment processing (if applicable)
   *   - extraData: Additional provider-specific data for payment processing
   *
   * @example
   * // Subscription payment
   * const subscriptionCheckout = await paymentService.checkoutV2({
   *   type: "subscription",
   *   subscriptionId: "sub_123456",
   *   currency: "INR",
   *   provider: "razorpay",
   * });
   *
   * @example
   * // TVOD (content purchase) payment
   * const tvodCheckout = await paymentService.checkoutV2({
   *   type: "tvod",
   *   productId: "movie_456",
   *   currency: "INR",
   *   provider: "razorpay",
   * });
   *
   * @throws {AxiosError} When the request fails due to network issues or server errors
   * @throws {ValidationError} When required parameters are missing or invalid
   */
  async checkoutV2(
    payload: ICheckoutV2
  ): Promise<AxiosResponse<ICheckoutResult>> {
    return this.request({
      method: "POST",
      url: "/api/v2/payments/checkout",
      data: payload,
    });
  }
}

export default PaymentService;
